"use strict";
/**
 * Document Operations
 *
 * Type-safe operations for editing aerospace documents.
 * AI emits these operations instead of raw HTML edits.
 *
 * This is the shared, authoritative version used by both frontend and backend.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.describeOperation = describeOperation;
exports.validateOperationStructure = validateOperationStructure;
exports.getOperationTargets = getOperationTargets;
exports.operationAffectsSection = operationAffectsSection;
// ===== Helper Functions =====
/**
 * Get human-readable description of an operation
 */
function describeOperation(op) {
    switch (op.op) {
        case 'update_text':
            return `Update text in ${op.targetId}`;
        case 'replace_block':
            return `Replace block ${op.targetId} with ${op.newBlock.type}`;
        case 'insert_block_after':
        case 'insert_block_before':
            return `Insert ${op.newBlock.type} ${op.op === 'insert_block_after' ? 'after' : 'before'} ${op.targetId}`;
        case 'insert_block_in_section':
            return `Insert ${op.newBlock.type} at ${op.position} of section ${op.sectionId}`;
        case 'delete_block':
            return `Delete block ${op.targetId}`;
        case 'insert_section_after':
        case 'insert_section_before':
            return `Insert section "${op.newSection.title}" ${op.op === 'insert_section_after' ? 'after' : 'before'} ${op.targetId}`;
        case 'delete_section':
            return `Delete section ${op.targetId}`;
        case 'move_section':
            return `Move section ${op.sectionId} to index ${op.newIndex}`;
        case 'update_section_title':
            return `Update section ${op.targetId} title to "${op.newTitle}"`;
        case 'update_table_cell':
            return `Update table ${op.tableId} cell [${op.rowId}, ${op.columnId}]`;
        case 'update_metadata':
            return `Update metadata field ${op.field}`;
        // Semantic operations (P1)
        case 'insert_after_content':
            return `Insert ${op.newBlock.type} after content matching "${op.searchText.substring(0, 30)}..."`;
        case 'insert_before_content':
            return `Insert ${op.newBlock.type} before content matching "${op.searchText.substring(0, 30)}..."`;
        // Bulk operations (P2)
        case 'append_to_all_subsections':
            return `Append ${op.newBlock.type} to all subsections of ${op.parentSection}`;
        case 'replace_all_text':
            return `Replace all "${op.searchText}" with "${op.replaceText}" in ${op.scope}`;
        case 'renumber_chapters':
            return `Renumber chapters with pattern "${op.pattern}" starting from ${op.startFrom || 1}`;
        default:
            return 'Unknown operation';
    }
}
/**
 * Validate operation has required fields
 * Returns validation result with detailed error messages
 */
function validateOperationStructure(op) {
    if (!op || typeof op !== 'object') {
        return { valid: false, error: 'Operation must be an object' };
    }
    if (!op.op || typeof op.op !== 'string') {
        return { valid: false, error: 'Operation must have an "op" field' };
    }
    // Validate specific operation types
    switch (op.op) {
        case 'update_text':
            if (!op.targetId || !op.newText) {
                return { valid: false, error: 'update_text requires targetId and newText' };
            }
            // Validate targetId is a block ID, not a section ID
            if (op.targetId.startsWith('sec-')) {
                return { valid: false, error: 'update_text requires a BLOCK ID (e.g., "p-1-1-1", "list-2-1"), not a section ID. Use update_section_title to change section titles.' };
            }
            break;
        case 'replace_block':
            if (!op.targetId || !op.newBlock) {
                return { valid: false, error: 'replace_block requires targetId and newBlock' };
            }
            // Validate targetId is a block ID, not a section ID
            if (op.targetId.startsWith('sec-')) {
                return { valid: false, error: 'replace_block requires a BLOCK ID (e.g., "p-1-1-1", "table-2-1"), not a section ID' };
            }
            // Validate newBlock structure
            if (!op.newBlock.id || typeof op.newBlock.id !== 'string') {
                return { valid: false, error: 'replace_block: newBlock must have a valid id field' };
            }
            if (!op.newBlock.type || typeof op.newBlock.type !== 'string') {
                return { valid: false, error: 'replace_block: newBlock must have a valid type field' };
            }
            if (!op.newBlock.content || typeof op.newBlock.content !== 'object') {
                return { valid: false, error: 'replace_block: newBlock must have a content field' };
            }
            break;
        case 'insert_block_after':
        case 'insert_block_before':
            if (!op.targetId || !op.sectionId || !op.newBlock) {
                return { valid: false, error: `${op.op} requires targetId, sectionId, and newBlock` };
            }
            // Validate targetId is a block ID, not a section ID
            if (op.targetId.startsWith('sec-')) {
                return { valid: false, error: `${op.op}: targetId must be a BLOCK ID (e.g., "p-1-1-1", "list-4-1"), not a section ID ("${op.targetId}"). Use traverse_document_graph to get block IDs first, then insert relative to a block.` };
            }
            // Validate newBlock structure
            if (!op.newBlock.id || typeof op.newBlock.id !== 'string') {
                return { valid: false, error: `${op.op}: newBlock must have a valid id field (e.g., "p-1-2-3")` };
            }
            if (!op.newBlock.type || typeof op.newBlock.type !== 'string') {
                return { valid: false, error: `${op.op}: newBlock must have a valid type field (e.g., "paragraph", "table")` };
            }
            if (!op.newBlock.content || typeof op.newBlock.content !== 'object') {
                return { valid: false, error: `${op.op}: newBlock must have a content object` };
            }
            break;
        case 'insert_block_in_section':
            if (!op.sectionId || !op.newBlock) {
                return { valid: false, error: 'insert_block_in_section requires sectionId and newBlock' };
            }
            if (!op.position || (op.position !== 'start' && op.position !== 'end')) {
                return { valid: false, error: 'insert_block_in_section requires position to be "start" or "end"' };
            }
            // Validate sectionId is a section ID
            if (!op.sectionId.startsWith('sec-')) {
                return { valid: false, error: `insert_block_in_section: sectionId must be a SECTION ID (e.g., "sec-4"), not "${op.sectionId}"` };
            }
            // Validate newBlock structure
            if (!op.newBlock.id || typeof op.newBlock.id !== 'string') {
                return { valid: false, error: 'insert_block_in_section: newBlock must have a valid id field (e.g., "p-4-1")' };
            }
            if (!op.newBlock.type || typeof op.newBlock.type !== 'string') {
                return { valid: false, error: 'insert_block_in_section: newBlock must have a valid type field (e.g., "paragraph")' };
            }
            if (!op.newBlock.content || typeof op.newBlock.content !== 'object') {
                return { valid: false, error: 'insert_block_in_section: newBlock must have a content object' };
            }
            break;
        case 'delete_block':
            if (!op.targetId || !op.sectionId) {
                return { valid: false, error: 'delete_block requires targetId and sectionId' };
            }
            // Validate targetId is a block ID, not a section ID
            if (op.targetId.startsWith('sec-')) {
                return { valid: false, error: 'delete_block: targetId must be a BLOCK ID (e.g., "p-1-1-1"), not a section ID. Use delete_section to remove sections.' };
            }
            break;
        case 'insert_section_after':
        case 'insert_section_before':
            if (!op.targetId || !op.newSection) {
                return { valid: false, error: `${op.op} requires targetId and newSection` };
            }
            break;
        case 'delete_section':
            if (!op.targetId) {
                return { valid: false, error: 'delete_section requires targetId' };
            }
            if (op.allowRequired === undefined) {
                return { valid: false, error: 'delete_section requires allowRequired field' };
            }
            break;
        case 'move_section':
            if (!op.sectionId || op.newIndex === undefined) {
                return { valid: false, error: 'move_section requires sectionId and newIndex' };
            }
            break;
        case 'update_section_title':
            if (!op.targetId || !op.newTitle) {
                return { valid: false, error: 'update_section_title requires targetId and newTitle' };
            }
            // Validate targetId is a section ID, not a block ID
            if (!op.targetId.startsWith('sec-')) {
                return { valid: false, error: 'update_section_title: targetId must be a SECTION ID (e.g., "sec-1-2"), not a block ID' };
            }
            break;
        case 'update_table_cell':
            if (!op.tableId || !op.rowId || !op.columnId || op.newValue === undefined) {
                return { valid: false, error: 'update_table_cell requires tableId, rowId, columnId, and newValue' };
            }
            break;
        case 'update_metadata':
            if (!op.field || op.newValue === undefined) {
                return { valid: false, error: 'update_metadata requires field and newValue' };
            }
            break;
        // Semantic operations (P1)
        case 'insert_after_content':
        case 'insert_before_content':
            if (!op.searchText || typeof op.searchText !== 'string') {
                return { valid: false, error: `${op.op} requires searchText` };
            }
            if (!op.searchType || (op.searchType !== 'semantic' && op.searchType !== 'exact')) {
                return { valid: false, error: `${op.op} requires searchType to be "semantic" or "exact"` };
            }
            if (!op.newBlock) {
                return { valid: false, error: `${op.op} requires newBlock` };
            }
            if (!op.newBlock.id || typeof op.newBlock.id !== 'string') {
                return { valid: false, error: `${op.op}: newBlock must have a valid id field` };
            }
            if (!op.newBlock.type || typeof op.newBlock.type !== 'string') {
                return { valid: false, error: `${op.op}: newBlock must have a valid type field` };
            }
            if (!op.newBlock.content || typeof op.newBlock.content !== 'object') {
                return { valid: false, error: `${op.op}: newBlock must have a content object` };
            }
            if (op.matchThreshold !== undefined && (typeof op.matchThreshold !== 'number' || op.matchThreshold < 0 || op.matchThreshold > 1)) {
                return { valid: false, error: `${op.op}: matchThreshold must be a number between 0 and 1` };
            }
            if (op.matchStrategy && !['first', 'last', 'all', 'ask'].includes(op.matchStrategy)) {
                return { valid: false, error: `${op.op}: matchStrategy must be "first", "last", "all", or "ask"` };
            }
            break;
        // Bulk operations (P2)
        case 'append_to_all_subsections':
            if (!op.parentSection || !op.parentSection.startsWith('sec-')) {
                return { valid: false, error: 'append_to_all_subsections requires a parentSection ID (e.g., "sec-4")' };
            }
            if (!op.newBlock) {
                return { valid: false, error: 'append_to_all_subsections requires newBlock' };
            }
            if (!op.newBlock.type || typeof op.newBlock.type !== 'string') {
                return { valid: false, error: 'append_to_all_subsections: newBlock must have a valid type field' };
            }
            if (!op.newBlock.content || typeof op.newBlock.content !== 'object') {
                return { valid: false, error: 'append_to_all_subsections: newBlock must have a content object' };
            }
            // Note: newBlock.id is auto-generated for each subsection
            break;
        case 'replace_all_text':
            if (!op.searchText || typeof op.searchText !== 'string') {
                return { valid: false, error: 'replace_all_text requires searchText' };
            }
            if (op.replaceText === undefined || typeof op.replaceText !== 'string') {
                return { valid: false, error: 'replace_all_text requires replaceText (can be empty string)' };
            }
            if (!op.scope || typeof op.scope !== 'string') {
                return { valid: false, error: 'replace_all_text requires scope ("document" or a section ID)' };
            }
            break;
        case 'renumber_chapters':
            if (!op.pattern || typeof op.pattern !== 'string') {
                return { valid: false, error: 'renumber_chapters requires pattern (e.g., "Chapter {n} - {title}")' };
            }
            if (!op.pattern.includes('{n}')) {
                return { valid: false, error: 'renumber_chapters pattern must include {n} placeholder for chapter number' };
            }
            if (op.startFrom !== undefined && (typeof op.startFrom !== 'number' || op.startFrom < 0)) {
                return { valid: false, error: 'renumber_chapters startFrom must be a non-negative number' };
            }
            break;
        default:
            return { valid: false, error: `Unknown operation type: ${op.op}` };
    }
    return { valid: true };
}
/**
 * Get all target IDs referenced by an operation
 */
function getOperationTargets(op) {
    const targets = [];
    switch (op.op) {
        case 'update_text':
        case 'replace_block':
        case 'delete_section':
        case 'update_section_title':
            targets.push(op.targetId);
            break;
        case 'insert_block_after':
        case 'insert_block_before':
        case 'delete_block':
            targets.push(op.targetId, op.sectionId);
            break;
        case 'insert_block_in_section':
            targets.push(op.sectionId);
            break;
        case 'insert_section_after':
        case 'insert_section_before':
            targets.push(op.targetId);
            break;
        case 'move_section':
            targets.push(op.sectionId);
            if (op.newParentId) {
                targets.push(op.newParentId);
            }
            break;
        case 'update_table_cell':
            targets.push(op.tableId);
            break;
        // Semantic operations - target determined at runtime via search
        case 'insert_after_content':
        case 'insert_before_content':
            if (op.sectionId) {
                targets.push(op.sectionId);
            }
            break;
        // Bulk operations
        case 'append_to_all_subsections':
            targets.push(op.parentSection);
            break;
        case 'replace_all_text':
            if (op.scope !== 'document') {
                targets.push(op.scope);
            }
            break;
        case 'renumber_chapters':
            // Whole-document operation, no specific target
            break;
    }
    return targets;
}
/**
 * Check if operation modifies a specific section
 */
function operationAffectsSection(op, sectionId) {
    const targets = getOperationTargets(op);
    if (targets.includes(sectionId)) {
        return true;
    }
    // Check if operation creates a new section
    if ((op.op === 'insert_section_after' || op.op === 'insert_section_before') &&
        op.newSection.id === sectionId) {
        return true;
    }
    return false;
}
//# sourceMappingURL=document-operations.js.map